import { useCallback, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { searchFlights } from 'api/flights'
import type { MappedFlightSearchResult } from 'api/flights'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import type { TransportOption } from 'tripPlanner/api/types'
import { parseSearchParams } from './flightShortlistUtils'

type RefreshStatus = 'idle' | 'refreshing' | 'success' | 'error'

export interface RefreshedFlight {
  option: TransportOption
  matchedJourney?: App.JourneyV2
  newPrice?: number
  newExpiry?: number
  isSoldOut: boolean
}

interface UseRefreshResult {
  status: RefreshStatus
  refreshedFlights: Array<RefreshedFlight>
  refresh: (options: Array<TransportOption>) => Promise<Array<RefreshedFlight>>
}

function useRefreshShortlistedFlights(): UseRefreshResult {
  const [status, setStatus] = useState<RefreshStatus>('idle')
  const [refreshedFlights, setRefreshedFlights] = useState<Array<RefreshedFlight>>([])

  const currency = useAppSelector(state => state.geo.currentCurrency)
  const region = useAppSelector(state => state.geo.currentRegionCode)

  const refresh = useCallback(async(options: Array<TransportOption>): Promise<Array<RefreshedFlight>> => {
    setStatus('refreshing')
    setRefreshedFlights([])

    const paramGroups = new Map<string, { params: FlightSearchParams; options: Array<TransportOption> }>()

    for (const option of options) {
      const data = option.savedItemData
      if (data?.source !== 'le_flight') continue

      const parsed = parseSearchParams(data.searchParams)
      if (!parsed) continue

      const key = JSON.stringify(parsed)
      const existing = paramGroups.get(key)
      if (existing) {
        existing.options.push(option)
      } else {
        paramGroups.set(key, { params: parsed, options: [option] })
      }
    }

    try {
      const results: Array<RefreshedFlight> = []

      for (const [, group] of paramGroups) {
        let searchResult: MappedFlightSearchResult
        try {
          searchResult = await searchFlights({
            itinerary: group.params.itinerary,
            currency,
            occupancies: group.params.occupancies,
            fareClass: group.params.fareClass,
            region,
            fareType: group.params.fareType,
          })
        } catch {
          for (const option of group.options) {
            results.push({ option, isSoldOut: false })
          }
          continue
        }

        const allFares = searchResult.onewayFares.flat()
        const newExpiry = Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000

        for (const option of group.options) {
          const data = option.savedItemData
          if (data?.source !== 'le_flight') continue

          const journeyKey = data.journeyKey
          const matched = journeyKey
            ? allFares.find(j => j.bookingInfo.journeyKey === journeyKey)
            : undefined

          if (matched) {
            results.push({
              option,
              matchedJourney: matched,
              newPrice: matched.price.adult.totalFare,
              newExpiry,
              isSoldOut: false,
            })
          } else {
            results.push({ option, isSoldOut: true })
          }
        }
      }

      setRefreshedFlights(results)
      setStatus('success')
      return results
    } catch {
      setStatus('error')
      return []
    }
  }, [currency, region])

  return { status, refreshedFlights, refresh }
}

export default useRefreshShortlistedFlights
